import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Row, Col, Space, Button, message } from "@mimber/ui-web"
import { Form, DatePickerField, TimezoneField } from "@mimber/forms"
import { useUpdateEvent } from "@mimber/client/mimber-organization"
import { EventEditScope } from "@mimber/schema"
import type { UpdateEventDto } from "@mimber/schema"
import {
  EditEventCalendarFormSchema,
  type EditEventCalendarFormData,
} from "./EditEventCalendarForm.schema"
import type { EditEventCalendarFormProps } from "./EditEventCalendarForm.types"

export const EditEventCalendarForm: React.FunctionComponent<
  EditEventCalendarFormProps
> = ({ event, onSuccess, onCancel }) => {
  const isInSeries = !!event.eventSeriesId

  const form = useForm<EditEventCalendarFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(EditEventCalendarFormSchema) as any,
    mode: "all",
    defaultValues: {
      startDate: event.startDate,
      endDate: event.endDate,
      timezone: event.timezone,
    },
  })

  const {
  } = form

  const mutation = useUpdateEvent(event.uuid, {
    onSuccess: () => {
      message.success("Event dates updated")
      onSuccess?.()
    },
    onError: (error) => {
      message.error(`Failed to update event: ${error.message}`)
    },
  })

  const onSubmit = (data: EditEventCalendarFormData) => {
    const payload: UpdateEventDto = {
      ...data,
      ...(isInSeries && { editScope: EventEditScope.THIS_EVENT }),
    }
    mutation.mutate(payload)
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending}
      layout="vertical"
    >
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <DatePickerField
            name="startDate"
            label="Start Date & Time"
            required
            showTime
          />
        </Col>
        <Col xs={24} sm={12}>
          <DatePickerField
            name="endDate"
            label="End Date & Time"
            required
            showTime
          />
        </Col>
      </Row>

      <TimezoneField name="timezone" label="Timezone" required />

      <Space
        style={{ justifyContent: "flex-end", width: "100%", marginTop: 16 }}
      >
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        <Button
          type="primary"
          htmlType="submit"
          loading={mutation.isPending}

        >
          Save Changes
        </Button>
      </Space>
    </Form>
  )
}
